import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { Filter, AlarmClock, Check, ArrowDownWideNarrow, Plus, X, BarChart3, CalendarDays } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { NewsSeverityBadge } from "@/components/NewsSeverityBadge";
import { AttachmentGrid } from "@/components/AttachmentPicker";
import { PRIORITY_RANK } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { ExportButtons, downloadNewsPdf } from "@/components/ExportButtons"; // Fix BS

// Fix B (2026-08-28): estado de resolución independiente del seguimiento con fecha.
const NEWS_STATUS_META = {
  abierta: { label: "Abierta", badge: "gray" },
  en_proceso: { label: "En proceso", badge: "amber" },
  concluida: { label: "Concluida", badge: "green" },
};

// Fix BQ (2026-11-03): quién cambió el estado, cargó el seguimiento y lo resolvió (pedido del cliente).
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", hourCycle: "h23", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
function NewsTrail({ n, full = false }) {
  const lines = [];
  if (full && n.status_history?.length) {
    n.status_history.forEach((h, i) => lines.push({ k: `h${i}`, t: `Estado "${NEWS_STATUS_META[h.status]?.label || h.status}" · ${h.by_name} · ${fmtWhen(h.at)}${h.notes ? ` · "${h.notes}"` : ""}` }));
  } else if (n.status_changed_by_name) {
    lines.push({ k: "st", t: `Estado "${NEWS_STATUS_META[n.status || "abierta"]?.label || n.status}" por ${n.status_changed_by_name} · ${fmtWhen(n.status_changed_at)}` });
  }
  if (n.followup_set_by_name) lines.push({ k: "fs", t: `Seguimiento cargado por ${n.followup_set_by_name} · ${fmtWhen(n.followup_set_at)}` });
  if (n.followup_resolved_by_name) lines.push({ k: "fr", t: `Seguimiento resuelto por ${n.followup_resolved_by_name} · ${fmtWhen(n.followup_resolved_at)}` });
  if (n.severity_corrected_by_name) lines.push({ k: "sv", t: `Prioridad corregida por ${n.severity_corrected_by_name} · ${fmtWhen(n.severity_corrected_at)}` });
  if (!lines.length) return null;
  return (
    <div style={{ fontSize: 11, color: "#0A0A0A", marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }} data-testid={`news-trail-${n.id}`}>
      {lines.map((l) => <span key={l.k}>{l.t}</span>)}
    </div>
  );
}

const MONTH_LABELS = { "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun", "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic" };
// Fix B (2026-08-28): NO usar Object.keys(MONTH_LABELS) — las claves "10"/"11"/"12"
// son numéricas y JS las reordena antes que "01".."09", rompiendo el orden Ene→Dic.
const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const thisYear = new Date().getFullYear();
const CHART_YEARS = [thisYear - 1, thisYear, thisYear + 1];
const AR_TZ = "America/Argentina/Buenos_Aires";
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: AR_TZ });

export default function AdminNews() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ client_id: "", supervisor_id: "", severity: "", q: "", date: "", overdue: false });
  const [subTab, setSubTab] = useState("pendientes");
  const [sortByPriority, setSortByPriority] = useState(false);
  const [followFor, setFollowFor] = useState(null);
  const [followForm, setFollowForm] = useState({ deadline: "", description: "" });
  const [detailFor, setDetailFor] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);
  const [chartYear, setChartYear] = useState(String(thisYear));
  const [chartMode, setChartMode] = useState("priority");

  const load = async () => {
    try {
      const [n, c] = await Promise.all([api.get("/news"), api.get("/clients")]);
      setList(n.data); setClients(c.data);
    } catch (e) { toast.error(formatApiError(e)); setList([]); }
    // /users es admin-only (403 para supervisor) -- se pide aparte para que
    // su fallo nunca bloquee la lista de novedades ni deje el skeleton eterno.
    try {
      const u = await api.get("/users");
      setUsers(u.data);
    } catch (e) { /* supervisor: filtro "Supervisor" queda vacío, sin romper la pantalla */ }
  };
  useEffect(() => { load(); }, []);

  const now = Date.now();
  const filtered = useMemo(() => {
    if (!list) return null;
    let out = list.filter((n) => {
      const status = n.status || "abierta";
      if (subTab === "pendientes" && status === "concluida") return false;
      if (subTab === "finalizadas" && status !== "concluida") return false;
      if (filters.client_id && n.client_id !== filters.client_id) return false;
      if (filters.supervisor_id && n.supervisor_id !== filters.supervisor_id) return false;
      if (filters.severity && n.severity !== filters.severity) return false;
      if (filters.q && !(`${n.title} ${n.description}`.toLowerCase().includes(filters.q.toLowerCase()))) return false;
      if (filters.date && dayKey(n.created_at) !== filters.date) return false;
      if (filters.overdue) {
        if (!n.followup_deadline || n.followup_resolved) return false;
        if (new Date(n.followup_deadline).getTime() > now) return false;
      }
      return true;
    });
    if (sortByPriority) {
      out = [...out].sort((a, b) => (PRIORITY_RANK[b.severity] || 0) - (PRIORITY_RANK[a.severity] || 0));
    }
    return out;
  }, [list, filters, now, sortByPriority, subTab]);

  // Fix B (2026-08-28): novedades por mes/prioridad, mismo criterio visual que Cronograma → Analíticas.
  const chartData = useMemo(() => {
    if (!list) return [];
    return MONTHS.map((m) => {
      const row = { month: MONTH_LABELS[m], info: 0, warning: 0, critical: 0 };
      for (const n of list) {
        const key = dayKey(n.created_at);
        if (key.slice(0, 4) === chartYear && key.slice(5, 7) === m) row[n.severity] = (row[n.severity] || 0) + 1;
      }
      return row;
    });
  }, [list, chartYear]);

  // Fix P (2026-09-09): misma vista pero separada por estado (abierta/en_proceso/concluida) en vez de prioridad.
  const chartDataStatus = useMemo(() => {
    if (!list) return [];
    return MONTHS.map((m) => {
      const row = { month: MONTH_LABELS[m], abierta: 0, en_proceso: 0, concluida: 0 };
      for (const n of list) {
        const key = dayKey(n.created_at);
        if (key.slice(0, 4) === chartYear && key.slice(5, 7) === m) {
          const status = n.status || "abierta";
          row[status] = (row[status] || 0) + 1;
        }
      }
      return row;
    });
  }, [list, chartYear]);

  const nameOf = (cid) => clients.find((c) => c.id === cid)?.name || "—";

  const submitFollow = async () => {
    if (!followForm.deadline || !followForm.description) return toast.error("Completá fecha y descripción");
    try {
      await api.patch(`/news/${followFor.id}/followup`, {
        deadline: new Date(followForm.deadline).toISOString(),
        followup_description: followForm.description,
      });
      toast.success("Seguimiento configurado");
      setFollowFor(null); setFollowForm({ deadline: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveNews = async (n) => {
    const note = window.prompt(`Notas de resolución para "${n.title}":`) || "";
    try { await api.post(`/news/${n.id}/resolve`, { resolution_notes: note }); toast.success("Novedad resuelta"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Fix B (2026-08-28): estado de resolución (abierta/en_proceso/concluida), no requiere seguimiento con fecha.
  const changeStatus = async (n, status) => {
    let notes;
    if (status === "concluida") {
      const input = window.prompt(`Observación opcional al concluir "${n.title}" (podés dejarlo vacío):`);
      if (input === null) return; // el usuario canceló
      notes = input || undefined;
    }
    try {
      const { data } = await api.patch(`/news/${n.id}/status`, { status, notes });
      setList((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      setDetailFor((d) => (d && d.id === data.id ? { ...d, ...data } : d));
      toast.success("Estado actualizado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const isOverdue = (n) => n.followup_deadline && !n.followup_resolved && new Date(n.followup_deadline).getTime() < now;

  const StatusControl = ({ n }) => {
    const key = n.status || "abierta";
    const meta = NEWS_STATUS_META[key];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span className={`badge ${meta.badge}`} data-testid={`news-status-${n.id}`}>{meta.label}</span>
        <select value={key} onChange={(e) => changeStatus(n, e.target.value)} style={{ fontSize: 10, padding: "3px 4px" }} data-testid={`news-status-select-${n.id}`}>
          <option value="abierta">Abierta</option>
          <option value="en_proceso">En proceso</option>
          <option value="concluida">Concluida</option>
        </select>
      </div>
    );
  };

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Operación</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 0", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Novedades</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <ExportButtons kind="news" rows={filtered} testIdPrefix="news" filtersLabel={[
            subTab === "pendientes" ? "Pendientes" : subTab === "finalizadas" ? "Finalizadas" : subTab,
            filters.client_id && `Objetivo: ${nameOf(filters.client_id)}`,
            filters.supervisor_id && `Cargó: ${users.find((u) => u.id === filters.supervisor_id)?.name || ""}`,
            filters.severity && `Prioridad: ${filters.severity === "critical" ? "Alta" : filters.severity === "warning" ? "Media" : "Baja"}`,
            filters.q && `Búsqueda: "${filters.q}"`,
            filters.date && `Fecha: ${filters.date.split("-").reverse().join("/")}`,
            filters.overdue && "Seguimiento vencido",
          ].filter(Boolean).join(" · ")} />
          <button onClick={() => setChartOpen((s) => !s)} className={chartOpen ? "btn-primary" : "btn-ghost"} data-testid="news-chart-toggle"><BarChart3 size={14} style={{ marginRight: 6 }} />Gráfico por mes</button>
          <Link to={`/${user?.role}/novedad`} className="btn-primary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }} data-testid="news-new-btn"><Plus size={14} style={{ marginRight: 6 }} />Nueva novedad</Link>
        </div>
      </div>
      <p style={{ fontSize: 13, color: "var(--invict-text-muted)", margin: "0 0 18px" }} data-testid="news-list-subtitle">Consultá y hacé seguimiento de las novedades cargadas.</p>

      {/* Fix H (2026-09-01): panel principal solo pendientes; concluidas van a su propia solapa. */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14, background: "var(--invict-bone-2)", padding: 4, borderRadius: 3, maxWidth: 340 }}>
        {[
          { k: "pendientes", label: "Pendientes" },
          { k: "finalizadas", label: "Finalizadas" },
        ].map((t) => (
          <button key={t.k} onClick={() => setSubTab(t.k)}
            style={{ flex: 1, padding: "9px", border: "none", background: subTab === t.k ? "white" : "transparent", color: subTab === t.k ? "#0A0A0A" : "var(--invict-text-muted)", fontWeight: 800, fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase", borderRadius: 2, cursor: "pointer", boxShadow: subTab === t.k ? "0 1px 4px rgba(0,0,0,0.05)" : "none" }}
            data-testid={`news-subtab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      {chartOpen && (
        <div className="card" style={{ padding: 16, marginBottom: 14 }} data-testid="news-chart-panel">
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 10, flexWrap: "wrap" }}>
            <div className="field" style={{ margin: 0, minWidth: 120 }}>
              <label>Año</label>
              <select value={chartYear} onChange={(e) => setChartYear(e.target.value)} data-testid="news-chart-year">
                {CHART_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="field" style={{ margin: 0, minWidth: 160 }}>
              <label>Ver por</label>
              <select value={chartMode} onChange={(e) => setChartMode(e.target.value)} data-testid="news-chart-mode">
                <option value="priority">Prioridad</option>
                <option value="status">Estado</option>
              </select>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartMode === "priority" ? (
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fontWeight: 700 }} />
                  <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="info" name="Baja" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="warning" name="Media" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="critical" name="Alta" fill="#BF3437" radius={[3, 3, 0, 0]} />
                </BarChart>
              ) : (
                <BarChart data={chartDataStatus} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} data-testid="news-chart-status">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fontWeight: 700 }} />
                  <YAxis tick={{ fontSize: 11 }} width={30} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="abierta" name="Abierta" fill="#9ca3af" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="en_proceso" name="En proceso" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="concluida" name="Concluida" fill="#16a34a" radius={[3, 3, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--invict-text-muted)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
          <Filter size={13} style={{ color: "#BF3437" }} />Filtros
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}><label>Cliente</label>
          <select value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}>
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}><label>Supervisor</label>
          <select value={filters.supervisor_id} onChange={(e) => setFilters({ ...filters, supervisor_id: e.target.value })}>
            <option value="">Todos</option>
            {users.filter((u) => u.role === "supervisor").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "0 0 160px" }}><label>Prioridad</label>
          <select value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })} data-testid="filter-severity">
            <option value="">Todas</option><option value="critical">Alta</option><option value="warning">Media</option><option value="info">Baja</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "0 0 160px" }}><label><CalendarDays size={11} style={{ display: "inline", marginRight: 3, verticalAlign: "-1px" }} />Día</label>
          <input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} data-testid="filter-date"/>
        </div>
        {filters.date && <button onClick={() => setFilters({ ...filters, date: "" })} className="btn-ghost" style={{ padding: "9px 12px", fontSize: 11, height: 39 }} data-testid="filter-date-clear">Limpiar día</button>}
        <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}><label>Buscar</label>
          <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="texto..."/>
        </div>
        <button onClick={() => setFilters({ ...filters, overdue: !filters.overdue })} className={filters.overdue ? "btn-primary" : "btn-ghost"} style={{ padding: "9px 12px", fontSize: 11, height: 39 }} data-testid="filter-overdue"><AlarmClock size={12} style={{ marginRight: 4 }}/>Vencidas</button>
        <button onClick={() => setSortByPriority((s) => !s)} className={sortByPriority ? "btn-primary" : "btn-ghost"} style={{ padding: "9px 12px", fontSize: 11, height: 39 }} data-testid="sort-by-priority"><ArrowDownWideNarrow size={12} style={{ marginRight: 4 }}/>Ordenar por prioridad</button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {filtered == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
        {filtered && filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }}>Sin novedades.</div>}
        {filtered && filtered.map((n) => (
          <div key={n.id} style={{ padding: "14px 20px", borderBottom: "1px solid var(--invict-bone-3)", borderLeft: isOverdue(n) ? "4px solid #BF3437" : "none" }} data-testid={`news-${n.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>{n.title}</div>
                {/* Fix B (2026-08-28): descripción recortada + "Ver más". Las novedades
                    largas quedaban apelmazadas e ilegibles en el listado. */}
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{n.description}</div>
                <button onClick={() => setDetailFor(n)} className="btn-ghost" style={{ padding: "3px 8px", fontSize: 10, marginTop: 6 }} data-testid={`news-expand-${n.id}`}>Ver novedad completa</button>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 6 }}>{nameOf(n.client_id)} · {n.supervisor_name}{n.supervisor_deleted && <DeletedUserBadge id={n.id} />} · {new Date(n.created_at).toLocaleString("es-AR")}</div>
                {n.attachments?.length > 0 && (
                  <div style={{ maxWidth: 260 }}><AttachmentGrid items={n.attachments} /></div>
                )}
                <NewsTrail n={n} />
                {n.followup_deadline && (
                  <div style={{ fontSize: 11, marginTop: 6, padding: "6px 10px", background: n.followup_resolved ? "rgba(22,163,74,0.08)" : isOverdue(n) ? "rgba(191,52,55,0.08)" : "var(--invict-bone-2)", borderLeft: `3px solid ${n.followup_resolved ? "#16a34a" : isOverdue(n) ? "#BF3437" : "#f59e0b"}` }}>
                    <b>Seguimiento:</b> {n.followup_description}<br/>
                    <span style={{ color: "var(--invict-text-muted)" }}>Deadline: {new Date(n.followup_deadline).toLocaleString("es-AR")}{n.followup_resolved ? " · ✓ RESUELTO" : isOverdue(n) ? " · ⚠ VENCIDO" : ""}</span>
                    {n.resolution_notes && <div style={{ fontStyle: "italic", marginTop: 3 }}>"{n.resolution_notes}"</div>}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <NewsSeverityBadge news={n} editable onUpdated={(updated) => setList((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))} />
                <StatusControl n={n} />
                {(n.status || "abierta") !== "concluida" && (
                  <button onClick={() => changeStatus(n, "concluida")} className="btn-ghost" style={{ padding: "4px 8px", fontSize: 10, color: "#16a34a", borderColor: "#16a34a" }} data-testid={`news-close-${n.id}`}><Check size={10} style={{ marginRight: 3 }}/>Cerrar novedad</button>
                )}
                {!n.followup_deadline && <button onClick={() => setFollowFor(n)} className="btn-ghost" style={{ padding: "4px 8px", fontSize: 10 }} data-testid={`news-track-${n.id}`}><AlarmClock size={10} style={{ marginRight: 3 }}/>Seguimiento</button>}
                {n.followup_deadline && !n.followup_resolved && <button onClick={() => resolveNews(n)} className="btn-ghost" style={{ padding: "4px 8px", fontSize: 10, color: "#16a34a", borderColor: "#16a34a" }} data-testid={`news-resolve-${n.id}`}><Check size={10} style={{ marginRight: 3 }}/>Resolver</button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {followFor && (
        <div className="modal-overlay" onClick={() => setFollowFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Seguimiento de novedad</h3>
            <p>"{followFor.title}" — definí qué hay que resolver y cuándo.</p>
            <div className="field"><label>Descripción del seguimiento *</label>
              <textarea value={followForm.description} onChange={(e) => setFollowForm({ ...followForm, description: e.target.value })} placeholder="Ej: el cliente debe reparar la cerca antes de..." data-testid="follow-desc"/>
            </div>
            <div className="field"><label>Deadline *</label>
              <input type="datetime-local" value={followForm.deadline} onChange={(e) => setFollowForm({ ...followForm, deadline: e.target.value })} data-testid="follow-deadline"/>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-ghost" onClick={() => setFollowFor(null)}>Cancelar</button>
              <button className="btn-primary" onClick={submitFollow} data-testid="follow-submit">Configurar seguimiento</button>
            </div>
          </div>
        </div>
      )}

      {detailFor && (
        <div className="modal-overlay" onClick={() => setDetailFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }} data-testid="news-detail-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <h3 style={{ margin: 0 }}>{detailFor.title}</h3>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => downloadNewsPdf(detailFor.id)} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }} data-testid="news-detail-pdf">Descargar PDF</button>
                <button onClick={() => setDetailFor(null)} className="btn-ghost" style={{ padding: 4 }} data-testid="news-detail-close"><X size={16} /></button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0" }}>
              <NewsSeverityBadge news={detailFor} />
              <StatusControl n={detailFor} />
            </div>
            <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 10 }}>
              {nameOf(detailFor.client_id)} · {detailFor.supervisor_name}{detailFor.supervisor_deleted && <DeletedUserBadge id={`detail-${detailFor.id}`} />} · {new Date(detailFor.created_at).toLocaleString("es-AR")}
            </div>
            <div style={{ fontSize: 13, whiteSpace: "pre-wrap", marginBottom: 12 }} data-testid="news-detail-description">{detailFor.description}</div>
            <div style={{ marginBottom: 12 }}><NewsTrail n={detailFor} full /></div>
            {detailFor.attachments?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--invict-text-muted)", marginBottom: 6 }}>Adjuntos</div>
                <AttachmentGrid items={detailFor.attachments} />
              </div>
            )}
            {detailFor.followup_deadline && (
              <div style={{ fontSize: 12, padding: "8px 10px", background: detailFor.followup_resolved ? "rgba(22,163,74,0.08)" : isOverdue(detailFor) ? "rgba(191,52,55,0.08)" : "var(--invict-bone-2)", borderLeft: `3px solid ${detailFor.followup_resolved ? "#16a34a" : isOverdue(detailFor) ? "#BF3437" : "#f59e0b"}` }}>
                <b>Seguimiento:</b> {detailFor.followup_description}<br/>
                <span style={{ color: "var(--invict-text-muted)" }}>Deadline: {new Date(detailFor.followup_deadline).toLocaleString("es-AR")}{detailFor.followup_resolved ? " · ✓ RESUELTO" : isOverdue(detailFor) ? " · ⚠ VENCIDO" : ""}</span>
                {detailFor.resolution_notes && <div style={{ fontStyle: "italic", marginTop: 3 }}>"{detailFor.resolution_notes}"</div>}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button className="btn-ghost" onClick={() => setDetailFor(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
